import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid';
import LoadingIndicatorLogo from '../../components/LoadingIndicatorLogo';
import { isNil } from '../../services/utils';
import { useAccount } from '../../contexts/Account';
import eventBus, { eventBusValues } from './../../eventBus';
import { getCompanyInfoBoardroomMenu } from './../../services/company_services';
import BoardSkeleton from '../../components/SkeletonLoading/boardSkeleton';
import BoardCard from '../../components/BoardCard';
import calendar from '../../icons/calendar.svg';

const useStyles = () => ({
  removed: {
    display: 'none',
  },
  advisorDetailsCardContainer: {
    marginBottom: '10px',
    marginRight: '0px',
    maxWidth: '380px',
  },
  infinteScrollContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  footerLoading: {
    height: '150px',
  },
});

function CompanyList(props) {
  const {
    onNoAdvisorsFound,
    companiesList,
    isLoading,
    incrementOffset,
    isMainCallLoading,
    doNotInfiniteScroll,
    isBoardSekeleton,
    emptySearchParams,
    lastFetch,
  } = props;
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const classes = useStyles();
  const [boardAdvisorsMap, setBoardAdvisorsMap] = useState();
  const [invitedAdvisorsMap, setInvitedAdvisorsMap] = useState();
  const location = useLocation();
  const peerPage = location.pathname === '/peer-directory';
  const { accountDetails } = useAccount();
  const companiesListFiltered = useMemo(() => {
    if (
      location.pathname.includes('/advisors') &&
      !location.pathname.includes('/advisors/')
    ) {
      return companiesList.filter(
        (company) =>
          !company.freeTrialEndsOn &&
          ['active', 'new'].includes(company.accountStatus)
      );
    }
    if (location.pathname.includes('/board-directory')) {
      const companiesToDisplay = [...companiesList];
      return companiesToDisplay;
    }
    return companiesList;
  }, [companiesList, lastFetch, emptySearchParams]);
  const loader = useRef();
  const clickedDetailCardMetric =
    location.pathname !== '/advisors' &&
    location.pathname !== '/advisors/search' &&
    location.pathname !== '/advisors/search'
      ? location.hash.substring(1) || 'answer'
      : false;
  const individualAdvisorPublicUrl = clickedDetailCardMetric
    ? location.pathname.substring(10)
    : false;
  const selectedAdvisor = clickedDetailCardMetric
    ? companiesList.find(
        ({ publicProfileUrl }) =>
          publicProfileUrl === individualAdvisorPublicUrl
      )
    : false;

  function handleButtonClick(company) {
    eventBus.dispatch(eventBusValues.triggerCompanyDrawerAction, {
      company,
      hidePostSection: false,
      companiesListFiltered,
    });
  }

  function handleButtonClickWithSlug(slug) {
    eventBus.dispatch(eventBusValues.triggerCompanyDrawerAction, {
      slug,
      hidePostSection: false,
      companiesListFiltered,
    });
  }

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get('invite')) {
      const companySample = accountDetails.boards.invitedBoards.find(
        (sample) => sample.slug === queryParams.get('invite')
      );
      if (companySample) {
        const company = companiesList.find(
          (item) => item.id === companySample.id
        );
        if (company) {
          handleButtonClick(company);
        } else {
          getCompanyInfoBoardroomMenu({ companyId: companySample.id })
            .then(({ data }) => {
              handleButtonClick(data.getCompany);
            })
            .catch((error) => {
              console.log(error);
            });
        }
      }
    }

    if (queryParams.get('view')) {
      const company = companiesList.find(
        (item) => item.slug === queryParams.get('view')
      );
      if (company) {
        handleButtonClick(company);
      } else {
        handleButtonClickWithSlug(queryParams.get('view'));
      }
    }
  }, []);

  const handleObserver = useCallback(
    (entries) => {
      const target = entries[0];
      if (target.isIntersecting && !isNil(incrementOffset)) {
        if (
          !isMainCallLoading &&
          (isNil(doNotInfiniteScroll) || !doNotInfiniteScroll)
        ) {
          incrementOffset();
        }
      }
    },
    [isMainCallLoading]
  );

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: '20px',
      threshold: 0,
    };
    const observer = new IntersectionObserver(handleObserver, option);
    if (loader.current) observer.observe(loader.current);
  }, [handleObserver, isMainCallLoading]);

  function getMarginRight(index) {
    if (isXS) return '0px';
    if (isSM) {
      if (index % 2 === 1) {
        return '0px';
      }
      return '16px';
    }
    if (index % 4 === 3) {
      return '0px';
    }
    return '16px';
  }

  if (onNoAdvisorsFound && !isLoading && companiesList.length === 0) {
    onNoAdvisorsFound();
  }

  if (!invitedAdvisorsMap) {
    const newInvitedAdvisorsMap = {};
    const { invitedAdvisors } = accountDetails;

    if (!invitedAdvisors) {
      setInvitedAdvisorsMap({});
      return null;
    }

    for (let i = 0; i < invitedAdvisors.length; i += 1) {
      newInvitedAdvisorsMap[invitedAdvisors[i].id] = {
        inviteDate: invitedAdvisors[i].inviteDate,
      };
    }

    setInvitedAdvisorsMap(newInvitedAdvisorsMap);
    return null;
  }

  if (!boardAdvisorsMap) {
    const newBoardAdvisorsMap = {};
    const { boardAdvisors } = accountDetails;

    if (!boardAdvisors) {
      setBoardAdvisorsMap({});
      return null;
    }

    for (let i = 0; i < boardAdvisors.length; i += 1) {
      newBoardAdvisorsMap[boardAdvisors[i].id] = {
        joinedOnDate: boardAdvisors[i].joinedOnDate,
      };
    }

    setBoardAdvisorsMap(newBoardAdvisorsMap);
    return null;
  }

  function goToRoute(route) {
    window.open(
      `${route}?iwm_advisor_id=${accountDetails.iwmId}&sosfcontactid=${accountDetails.salesforceContactId}&email=${accountDetails.email}&plancode=${accountDetails.recurlyPlanCode}`
    );
  }

  return (
    <>
      <div
        style={
          clickedDetailCardMetric && selectedAdvisor ? classes.removed : {}
        }
      >
        <Grid container>
          {companiesListFiltered.length > 0 &&
            companiesListFiltered.map((company, index) => {
              return (
                <Grid key={company.id} item xxs={12} xs={6} sm={4} md={3}>
                  <Box
                    sx={{
                      marginBottom: '20px',
                      marginRight: getMarginRight(index),
                      maxWidth: isSM ? '100%' : '400px',
                    }}
                  >
                    <BoardCard
                      key={company.id}
                      board={company}
                      fullWidth
                      hideMemberSince
                      hideHeadline={peerPage}
                      bigCompanyName={peerPage}
                      companyList
                      onClick={() => handleButtonClick(company)}
                      displayPillsFlag
                    />
                  </Box>
                </Grid>
              );
            })}
          <Grid item xxs={12} xs={6} sm={4} md={3}>
            <Box
              sx={{
                marginBottom: '20px',
                marginRight: getMarginRight(companiesListFiltered.length),
                maxWidth: isSM ? '100%' : '400px',
              }}
            >
              <BoardCard
                board={{
                  companyName: 'Start Your Own Board',
                  headline:
                    'Included with your membership is the ability to start your own advisory board for a company, team or personal side project.',
                }}
                buttonText="Get Started"
                hideMemberSince
                companyList
                staticTile
                fullWidth
                customImage={calendar}
                headlineMarginBottom="40px"
                onClick={() =>
                  goToRoute(
                    'https://register.advisorycloud.com/platform/upgrade-professional-annual/'
                  )
                }
              />
            </Box>
          </Grid>
        </Grid>

        <div style={classes.footerLoading}>
          {isMainCallLoading &&
            (isNil(doNotInfiniteScroll) || !doNotInfiniteScroll) && (
              <>
                {isBoardSekeleton ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: {
                        xxs: 'center',
                        sm: 'space-around',
                      },
                      flexWrap: 'wrap',
                      gap: 2,
                    }}
                  >
                    <BoardSkeleton />
                    <BoardSkeleton />
                    <BoardSkeleton />
                  </Box>
                ) : (
                  <div style={classes.infinteScrollContainer}>
                    <LoadingIndicatorLogo
                      size={100}
                      iconFontSize={47}
                      iconRight={49}
                    />
                  </div>
                )}
              </>
            )}
        </div>

        <div ref={loader} />
      </div>
    </>
  );
}
export default CompanyList;
